import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi, ApiUser, UserType } from '../services/api.ts';
import { useSnackbar } from './SnackbarContext.tsx';

// Only these roles are allowed to use the inventory app
const ALLOWED_ROLES: UserType[] = ['admin', 'bartender'];

const isAllowedRole = (user: ApiUser): boolean => ALLOWED_ROLES.includes(user.type);

interface AuthContextType {
  user: ApiUser | null;
  sessionToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<ApiUser | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { showError, showSuccess } = useSnackbar();

  const isAuthenticated = !!user && !!sessionToken;

  // Check for existing session on app load
  useEffect(() => {
    const checkExistingSession = async () => {
      const storedToken = localStorage.getItem('sessionToken');
      if (storedToken) {
        try {
          const data = await authApi.validate(storedToken);
          if (!isAllowedRole(data.user)) {
            localStorage.removeItem('sessionToken');
          } else {
            setUser(data.user);
            setSessionToken(storedToken);
          }
        } catch (err) {
          localStorage.removeItem('sessionToken');
        }
      }
      setIsLoading(false);
    };

    checkExistingSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);

    try {
      const data = await authApi.login({ email, password });

      if (!isAllowedRole(data.user)) {
        // Invalidate the session that was just created for a disallowed role
        try {
          await authApi.logout(data.sessionToken);
        } catch (logoutErr) {
          console.error('Failed to invalidate session:', logoutErr);
        }
        throw new Error('Access denied: only admin and bartender accounts can use the inventory app');
      }

      setUser(data.user);
      setSessionToken(data.sessionToken);
      localStorage.setItem('sessionToken', data.sessionToken);
      showSuccess('Login successful!');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      showError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    if (sessionToken) {
      try {
        await authApi.logout(sessionToken);
        showSuccess('Logged out successfully!');
      } catch (err) {
        console.error('Logout error:', err);
        showError('Error during logout');
      }
    }

    setUser(null);
    setSessionToken(null);
    localStorage.removeItem('sessionToken');
  };

  const value: AuthContextType = {
    user,
    sessionToken,
    isAuthenticated,
    isLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
